import React, { useState } from 'react';
import { SUPPORTED_LANGUAGES } from '../../utils/constants';
import { ApiService } from '../../services/api';
import { auth } from '../../services/firebase';
import './DubbingConfig.css';

interface DubbingConfigProps {
  videoPath: string;
  videoName: string;
  onJobCreated: (jobId: string) => void;
  onCancel: () => void;
}

const DubbingConfig: React.FC<DubbingConfigProps> = ({
  videoPath,
  videoName,
  onJobCreated,
  onCancel
}) => {
  const [sourceLanguage, setSourceLanguage] = useState('en-US');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [enableDiarization, setEnableDiarization] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleTargetLanguageToggle = (lang: string) => {
    setTargetLanguages(prev => {
      if (prev.includes(lang)) {
        return prev.filter(l => l !== lang);
      } else {
        return [...prev, lang];
      }
    });
  };

  const handleSubmit = async () => {
    if (targetLanguages.length === 0) {
      setError('Please select at least one target language');
      return;
    }

    if (targetLanguages.includes(sourceLanguage)) {
      setError('Target language cannot be the same as source language');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/jobs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
          },
          body: JSON.stringify({
            originalVideoPath: videoPath,
            originalVideoName: videoName,
            sourceLanguage,
            targetLanguages,
            enableDiarization
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create job');
      }

      const { data } = await response.json();
      onJobCreated(data.id);
    } catch (error: any) {
      console.error('Error creating job:', error);
      setError(error.message || 'Failed to create dubbing job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out commonly used languages for easier selection
  const commonLanguages = ['en-US', 'es-US', 'fr-FR', 'de-DE', 'it-IT', 'pt-BR', 'ja-JP', 'ko-KR', 'zh-CN'];
  const otherLanguages = Object.keys(SUPPORTED_LANGUAGES).filter(lang => !commonLanguages.includes(lang));

  return (
    <div className="dubbing-config">
      <h3>Configure Dubbing Settings</h3>
      
      <div className="config-section">
        <label className="config-label">Video File</label>
        <div className="video-info">
          <span className="video-name">🎬 {videoName}</span>
        </div>
      </div>

      <div className="config-section">
        <label className="config-label" htmlFor="source-language">
          Source Language
        </label>
        <select
          id="source-language"
          value={sourceLanguage}
          onChange={(e) => setSourceLanguage(e.target.value)}
          className="language-select"
          disabled={isSubmitting}
        >
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>

      <div className="config-section">
        <label className="config-label">
          Target Languages
          <span className="label-hint">Select one or more languages for dubbing</span>
        </label>
        
        <div className="language-group">
          <h4>Common Languages</h4>
          <div className="language-grid">
            {commonLanguages.map(lang => (
              <label
                key={lang}
                className={`language-checkbox ${lang === sourceLanguage ? 'disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={targetLanguages.includes(lang)}
                  onChange={() => handleTargetLanguageToggle(lang)}
                  disabled={isSubmitting || lang === sourceLanguage}
                />
                <span>{SUPPORTED_LANGUAGES[lang]}</span>
              </label>
            ))}
          </div>
        </div>

        {otherLanguages.length > 0 && (
          <div className="language-group">
            <h4>Other Languages</h4>
            <div className="language-grid">
              {otherLanguages.map(lang => (
                <label
                  key={lang}
                  className={`language-checkbox ${lang === sourceLanguage ? 'disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={targetLanguages.includes(lang)}
                    onChange={() => handleTargetLanguageToggle(lang)}
                    disabled={isSubmitting || lang === sourceLanguage}
                  />
                  <span>{SUPPORTED_LANGUAGES[lang]}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {targetLanguages.length > 0 && (
          <div className="selected-languages">
            <strong>Selected:</strong> {targetLanguages.map(lang => SUPPORTED_LANGUAGES[lang]).join(', ')}
          </div>
        )}
      </div>

      <div className="config-section">
        <label className="config-checkbox">
          <input
            type="checkbox"
            checked={enableDiarization}
            onChange={(e) => setEnableDiarization(e.target.checked)}
            disabled={isSubmitting}
          />
          <span>
            Enable Speaker Diarization
            <span className="checkbox-hint">Identify and separate different speakers in the video</span>
          </span>
        </label>
      </div>

      {error && (
        <div className="config-error">
          ⚠️ {error}
        </div>
      )}

      <div className="config-actions">
        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitting || targetLanguages.length === 0}
        >
          {isSubmitting ? 'Creating Job...' : 'Start Dubbing'}
        </button>
        <button
          className="cancel-button"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DubbingConfig;